// FILE: apps/admin/src/components/UserAvatar.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Render user profile avatar (customPhotoUrl or Telegram photoUrl) with deterministic initials fallback and display name helper for the Admin Panel.
//   SCOPE: UserAvatar component and resolveAdminUserDisplayName helper.
//   DEPENDS: none
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   UserAvatar - Circular avatar component showing custom/Telegram photo or crisp initials fallback
//   resolveAdminUserDisplayName - Compute primary display name and optional original Telegram name subtitle
// END_MODULE_MAP

import { useState } from 'react';

// START_CONTRACT: resolveAdminUserDisplayName
//   PURPOSE: Resolve primary display name (customName -> firstName -> @username -> fallback) and whether customName overrides Telegram firstName.
//   INPUTS: { user: { customName?: string | null; firstName?: string | null; username?: string | null; telegramId?: number | null } }
//   OUTPUTS: { { primary: string; originalTgName: string | null; isCustomName: boolean } }
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: resolveAdminUserDisplayName
export function resolveAdminUserDisplayName(user: {
  customName?: string | null;
  firstName?: string | null;
  username?: string | null;
  telegramId?: number | null;
}): { primary: string; originalTgName: string | null; isCustomName: boolean } {
  // START_BLOCK_RESOLVE_ADMIN_USER_DISPLAY_NAME
  const custom = user.customName?.trim() || null;
  const first = user.firstName?.trim() || null;
  const primary =
    custom ||
    first ||
    (user.username ? `@${user.username}` : user.telegramId ? `#${user.telegramId}` : 'Пользователь');
  const isCustomName = Boolean(custom && custom !== first);
  return {
    primary,
    originalTgName: isCustomName ? first : null,
    isCustomName,
  };
  // END_BLOCK_RESOLVE_ADMIN_USER_DISPLAY_NAME
}

// START_CONTRACT: UserAvatar
//   PURPOSE: Render circular user avatar image (customPhotoUrl || photoUrl) with automatic fallback to initials on broken/missing URLs.
//   INPUTS: { photoUrl?: string | null; customPhotoUrl?: string | null; name?: string | null; size?: number }
//   OUTPUTS: React element
//   SIDE_EFFECTS: Tracks image load error state locally
//   LINKS: M-ADMIN
// END_CONTRACT: UserAvatar
export function UserAvatar({
  photoUrl,
  customPhotoUrl,
  name,
  size = 34,
}: {
  photoUrl?: string | null;
  customPhotoUrl?: string | null;
  name?: string | null;
  size?: number;
}) {
  // START_BLOCK_USER_AVATAR
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const src = (customPhotoUrl?.trim() || photoUrl?.trim() || '') || null;
  const showImg = Boolean(src && failedSrc !== src);

  const clean = (name ?? '').replace(/^@/, '').trim();
  const initials = clean
    ? clean
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p.slice(0, 1).toUpperCase())
        .join('')
    : 'U';

  return (
    <span
      className="user-avatar-circle"
      style={{
        width: size,
        height: size,
        fontSize: Math.max(11, Math.round(size * 0.38)),
      }}
    >
      {showImg && src ? (
        <img
          src={src}
          alt={clean || 'avatar'}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailedSrc(src)}
        />
      ) : (
        <span>{initials}</span>
      )}
    </span>
  );
  // END_BLOCK_USER_AVATAR
}
